import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import LookatPreview from "../../features/previews/lookat/LookatPreview";
import LookatNodePreview from "../../features/sequence/node_previews/LookatNodePreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import { getLookAtSelfWarning } from "./MessageBubble";
import {
  compactSearchableStrings,
  SearchLookupContext,
} from "./SearchableInteractionValues";

export interface ILookatConfig {
  sourceNpcId: string;
  targetId: string;
  /** Look with the head only (FinalIK); missing/false = whole body. */
  finalIK?: boolean;
}

export interface ILookat extends Interaction {
  lookats: ILookatConfig[];
}

export const lookatInteraction = (): InteractionFactoryEntity => {
  const view = <LookatPreview />;
  const create = (getKeys: (amount: number) => string[]): ILookat => {
    return {
      id: GetNewGuid(),
      name: "Look At",
      type: InteractionType.Lookat,
      lookats: [
        {
          targetId: "",
          sourceNpcId: "",
        },
      ],
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): ILookat => {
    return interaction as ILookat;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
    getAnswerArray: (originalAnswers: IAnswer[], newKeys: string[]) => IAnswer[]
  ): ILookat => {
    const fromLookat = interaction as ILookat;

    return {
      id: GetNewGuid(),
      name: fromLookat.name,
      type: fromLookat.type,
      lookats: [...fromLookat.lookats],
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[]
  ): string[] => {
    return [];
  };

  const validateErrors = (interaction: Interaction): string[] => {
    const converted = interaction as ILookat;
    const errors: string[] = [];

    for (let i = 0; i < converted.lookats.length; i++) {
      const lookat = converted.lookats[i];

      if (!lookat.sourceNpcId) errors.push(`Actor ${i + 1} is empty`);
      if (!lookat.targetId) errors.push(`Target ${i + 1} is empty`);
      // Timeline lookats store actor ids as targets; in sequences the target
      // is a position id, so this never matches there.
      if (
        lookat.sourceNpcId &&
        lookat.targetId &&
        lookat.targetId === lookat.sourceNpcId
      )
        errors.push(getLookAtSelfWarning(`Actor ${i + 1}`));
    }

    return errors;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[],
    _getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const converted = interaction as ILookat;
    const searchableStrings = compactSearchableStrings(
      interaction.name,
      converted.lookats.flatMap((lookat) =>
        compactSearchableStrings(
          searchLookupContext?.getActorSearchTerms(lookat.sourceNpcId) ??
            lookat.sourceNpcId,
          searchLookupContext?.getObjectSearchTerms(lookat.targetId) ??
            lookat.targetId,
        ),
      ),
    );

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType
    );
  };

  const filterType: string = "LookAt";

  return {
    View: view,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <LookatNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    ValidateErrors: validateErrors,
    Filter: filter,
  };
};
